import { Component, Input } from '@angular/core';
import * as Models from '../../../models/report-view.models';
import { lightenColor, parsePercent, COLOR_PALETTE } from './ranked-horizontal-bar-chart.utils';

@Component({
  selector: 'ranked-horizontal-bar-chart',
  templateUrl: './ranked-horizontal-bar-chart.component.html',
  styleUrls: ['../panel-elements.scss', './ranked-horizontal-bar-chart.component.scss'],
  standalone: false
})
export class RankedHorizontalBarChartComponent implements Models.ElementComponent {
  @Input() set dataSets(value: Models.DataSet[]) {
    if (!!value && value.length >= 1) {
      this.processData(value[0]);
      this.isDataSetLoaded = true;
    } else {
      this.headerColumns = [];
      this.bodyRows = [];
      this.barMeta = [];
      this.isDataSetLoaded = false;
    }
  }
  @Input() settings: Models.ElementSettings;

  isDataSetLoaded = false;

  headerColumns: Models.RowValue[] = [];
  bodyRows: Models.RowValue[][] = [];
  barMeta: Array<{ pct: number; color: string; light: string; label: string }> = [];
  // Skeleton loader data
  skeletonColumns: string[] = ['col1', 'col2', 'col3', 'col4'];
  placeholderData: any[] = Array.from({ length: 6 }, () => ({}));

  // Hover / tooltip state
  hoverMode: 'bar' | 'title' | null = null;
  barHoverIndex: number | null = null;
  titleHoverIndex: number | null = null;
  tooltipX = 0; // pageX offset for positioning
  tooltipY = 0; // pageY offset

  // Bar hover handlers (percentage tooltip)
  onBarEnter(e: MouseEvent, rowIndex: number) { this.barHoverIndex = rowIndex; this.hoverMode = 'bar'; this.updateTooltipPosition(e); }
  onBarMove(e: MouseEvent) { if (this.hoverMode === 'bar') this.updateTooltipPosition(e); }
  onBarLeave() { if (this.hoverMode === 'bar') { this.barHoverIndex = null; this.hoverMode = null; } }

  // Title hover handlers (full label tooltip)
  onTitleEnter(e: MouseEvent, rowIndex: number) { this.titleHoverIndex = rowIndex; this.hoverMode = 'title'; this.updateTooltipPosition(e); }
  onTitleMove(e: MouseEvent) { if (this.hoverMode === 'title') this.updateTooltipPosition(e); }
  onTitleLeave() { if (this.hoverMode === 'title') { this.titleHoverIndex = null; this.hoverMode = null; } }

  get barHoverTooltip() {
    if (this.hoverMode !== 'bar' || this.barHoverIndex == null) return null;
    const row = this.bodyRows[this.barHoverIndex];
    const meta = this.barMeta[this.barHoverIndex];
    if (!row || !meta) return null;
    return { network: row[1]?.label ?? '', percentLabel: meta.label, color: meta.color };
  }

  get titleHoverTooltip() {
    if (this.hoverMode !== 'title' || this.titleHoverIndex == null) return null;
    const row = this.bodyRows[this.titleHoverIndex];
    if (!row) return null;
    return { title: row[1]?.label || row[1]?.value };
  }

  private updateTooltipPosition(e: MouseEvent) { this.tooltipX = e.clientX + 12; this.tooltipY = e.clientY + 12; }

  /**
   * Convert incoming dataset into header/body arrays and build parallel bar metadata used by the template.
   * @param dataSet Single dataset
   *
   * Expected configuration (convention):
   *  - settings.dimensionNames[0] => rank dimension column name
   *  - settings.dimensionNames[1] => network (label) dimension column name
   *  - settings.metricName (or settings.metrics[0].name) => percentage metric column name
   */
  private processData(dataSet: Models.DataSet): void {
    if (!dataSet?.rows?.length || !dataSet?.columns?.length) {
      this.headerColumns = []; this.bodyRows = []; this.barMeta = []; return;
    }

    // Resolve configured column names
    const [rankDimName, networkDimName] = this.settings?.dimensionNames || [];
    let metricName = this.settings?.metricName;
    if (!metricName && this.settings?.metrics?.length) metricName = this.settings.metrics[0].name; // fallback

    if (!rankDimName || !networkDimName || !metricName) {
      console.error('RankedHorizontalBarChartComponent missing required settings: dimensionNames[0], dimensionNames[1], metricName');
      this.headerColumns = []; this.bodyRows = []; this.barMeta = []; return;
    }

    // Support plain JSON objects (no prototype) by NOT relying on DataSet instance methods
    const getIndex = (colName: string) => dataSet.columns.findIndex(c => c.name === colName);
    const rankIndex = getIndex(rankDimName);
    const networkIndex = getIndex(networkDimName);
    const metricIndex = getIndex(metricName);

    if ([rankIndex, networkIndex, metricIndex].some(i => i === -1)) {
      console.error('RankedHorizontalBarChartComponent could not locate one or more configured columns', { rankDimName, networkDimName, metricName, dataSet });
      this.headerColumns = []; this.bodyRows = []; this.barMeta = []; return;
    }

    const rankCol = dataSet.columns[rankIndex];
    const networkCol = dataSet.columns[networkIndex];
    const metricCol = dataSet.columns[metricIndex];

    // Build header columns explicitly (only the three we care about for this element)
    this.headerColumns = [
      { label: rankCol.displayName || rankCol.name || 'Rank', value: rankCol.name, formatter: '' },
      { label: networkCol.displayName || networkCol.name || 'Network', value: networkCol.name, formatter: '' },
      { label: metricCol.displayName || metricCol.name || '%', value: metricCol.name, formatter: metricCol.type === 'metric' ? metricCol.name : '' }
    ];

    const sourceRows = dataSet.rows;

    // Map to only selected columns; cap at 15 rows.
    this.bodyRows = sourceRows.map(r => [r[rankIndex], r[networkIndex], r[metricIndex]]);

    this.barMeta = this.bodyRows.map((r, i) => {
      const cell = r[2]; // metric cell in trimmed row structure
      const pct = parsePercent(cell?.value, cell?.label);
      const color = COLOR_PALETTE[i % COLOR_PALETTE.length];
      return { pct, color, light: lightenColor(color, 0.55), label: pct.toFixed(1) + '%' };
    });
  }
}
